<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useQuery } from '@tanstack/vue-query'
  import { storeToRefs } from 'pinia'

  import { useContentApi } from '@/composables/useCmsContentApi'
  import useRelation from '@/composables/useRelation'
  import { useAuthStore } from '@/stores/auth'
  import { VgsDrawerColorScheme } from '@/enums/VgsDrawerColorScheme'
  import type { ExpandElement as ExpandElementType } from '@/types/ExpandElement'
  import type { LongformText } from '@/types/LongformText'
  import type { StepTask } from '@/types/Task'
  import type { TeacherGuide } from '@/types/TeacherGuide'
  import type { TextImage } from '@/types/TextImage'

  import SignatureIcon from '@/components/icons/SignatureIcon.vue'

  import ExpandElement from '@/components/global/ExpandElement.vue'
  import ExternalLink from '@/components/global/ExternalLink.vue'
  import IbexaContentTypeIdentifier from '@/enums/IbexaContentTypeIdentifier'
  import LongformTextBlock from '@/components/LongformTextBlock.vue'
  import RichTextRenderer from '@/components/common/RichTextRenderer.vue'
  import Subtasks from '@/components/task/Subtasks.vue'
  import TextImageElement from '@/components/TextImageElement.vue'
  import Typography from '@/components/common/Typography.vue'
  import VgsButton from '@/components/common/VgsButton.vue'
  import VgsDrawer from '@/components/common/VgsDrawer.vue'
  import VgsSpinner from '@/components/common/VgsSpinner.vue'
  import { removeSoftHyphen } from '../../utils'

  const props = defineProps<{
    task: StepTask
    currentPage: number
    totalPages: number
  }>()
  const { t } = useI18n()
  const { getRelatedContent } = useRelation()
  const { getAllExternalLinks, getExpandElementChildren, getTeacherGuideChildren } = useContentApi()

  const { isTeacher } = storeToRefs(useAuthStore())
  const relatedResources = ref<(LongformText | TextImage)[]>([])
  const expandElements = ref<ExpandElementType[]>([])
  const currentRelatedResource = ref<TextImage | LongformText | null>(null)
  const teacherGuide = ref<TeacherGuide | null>(null)
  const isLoading = ref<boolean>(false)
  const isDrawerOpen = ref<boolean>(false)
  const drawerTitle = ref<string>('')
  const drawerBody = ref<string>('')
  const drawerColorScheme = ref<VgsDrawerColorScheme>(VgsDrawerColorScheme.Black)

  onMounted(async () => {
    isLoading.value = true
    try {
      relatedResources.value = await getRelatedContent(props.task.relatedResourceContentIds)
    } finally {
      isLoading.value = false
    }

    expandElements.value = await getExpandElementChildren(props.task.locationId)
  })

  const externalLinks = useQuery({
    queryKey: ['externalLinks', props.task.locationId],
    queryFn: async () => await getAllExternalLinks(props.task.locationId),
    staleTime: Infinity,
  })

  const currentRelatedResourceComponentName = computed(() => {
    switch (currentRelatedResource?.value?.contentTypeIdentifier) {
    case IbexaContentTypeIdentifier.vgsLongformText:
      return LongformTextBlock
    case IbexaContentTypeIdentifier.vgsTextImageElement:
      return TextImageElement
    default:
      return ''
    }
  })

  onMounted(async () => {
    if (!isTeacher) return
    teacherGuide.value = await getTeacherGuideChildren(props.task.contentId)
  })

  const handleTeacherGuideClick = () => {
    if (!teacherGuide.value) return

    drawerBody.value = teacherGuide.value.description
    drawerTitle.value = teacherGuide.value.title
    drawerColorScheme.value = VgsDrawerColorScheme.Violet
    isDrawerOpen.value = true
  }

  const handleHintClick = (content: string) => {
    isDrawerOpen.value = true
    drawerBody.value = content
    drawerTitle.value = 'Hint'
  }

  const handleRelatedSubjectClick = async (resource: TextImage | LongformText) => {
    isDrawerOpen.value = true
    drawerTitle.value = ''
    currentRelatedResource.value = resource
  }

  const handleClose = () => {
    isDrawerOpen.value = false
    drawerTitle.value = ''
    drawerBody.value = ''
    drawerColorScheme.value = VgsDrawerColorScheme.Black
    currentRelatedResource.value = null
  }
</script>

<template>
  <div class="step-task-content">
    <Typography
      v-if="totalPages && totalPages > 1"
      class="counter"
      component="span"
      variant="h4"
    >
      {{ task.isStepSuperTitle ? t('task.stepTitle') : t('task.taskTitle') }}
      <span>
        {{ `${currentPage ?? 1} av ${totalPages ?? 1}` }}
      </span>
    </Typography>
    <div class="description">
      <Typography
        variant="h2"
        class="title"
      >
        {{ task.title }}
      </Typography>
      <VgsButton
        v-if="teacherGuide && isTeacher"
        variant="flat-sharp"
        data-track="enkeltoppgave-lærerveiledning-knapp"
        @click="handleTeacherGuideClick"
      >
        {{ t('subject.to-teacher') }}
      </VgsButton>
      <SignatureIcon
        v-if="task?.isNoAidTask"
        class="icon"
      />
      <RichTextRenderer
        v-if="task.description"
        :rich-text="task.description"
      />
    </div>

    <Subtasks :subtasks="task.subtasks" />

    <RichTextRenderer
      v-if="task.descriptionAfterSubtasks"
      :rich-text="task.descriptionAfterSubtasks"
    />

    <div
      v-if="externalLinks.data.value"
      class="external-link-block"
    >
      <ExternalLink
        v-for="element in externalLinks.data.value"
        :key="element.locationId"
        :element="element"
      />
    </div>
    <!-- TODO: style/replace hr -->
    <hr v-if="relatedResources?.length || task?.hint">
    <div
      v-if="relatedResources?.length || task?.hint"
      class="help"
    >
      <Typography
        variant="caption"
        class="help-title"
      >
        {{ t('task.needHelp') }}
      </Typography>
      <VgsSpinner
        :is-loading="isLoading"
        :is-inline="true"
      />
      <VgsButton
        v-for="(relatedResource, index) in relatedResources"
        :key="index"
        variant="flat-sharp"
        data-track="enkeltoppgave-relatert-fagstoff-knapp"
        class="to-teacher-button"
        @click="() => handleRelatedSubjectClick(relatedResource)"
      >
        {{ removeSoftHyphen(relatedResource?.title ?? '') }}
      </VgsButton>
      <VgsButton
        v-if="task?.hint"
        variant="flat-sharp"
        data-track="enkeltoppgave-hint-knapp"
        @click="() => handleHintClick(task.hint)"
      >
        Hint
      </VgsButton>
    </div>
    <!-- TODO: style/replace hr -->
    <hr v-if="expandElements.length">
    <ExpandElement
      v-for="(expandElement, index) in expandElements"
      :key="index"
      :index="index"
      :element="expandElement"
      data-track="enkeltoppgave-utvidbart-element"
      class="expand-element"
    />
  </div>

  <VgsDrawer
    :color-scheme="drawerColorScheme"
    :title="drawerTitle"
    :open="isDrawerOpen"
    @close="handleClose"
  >
    <component
      :is="currentRelatedResourceComponentName"
      v-if="currentRelatedResource"
      :element="currentRelatedResource"
    />
    <LongformTextBlock
      v-if="drawerBody"
      :element="{ richText: drawerBody } as LongformText"
    />
  </VgsDrawer>
</template>

<style scoped lang="scss">
  .step-task-content {
    max-width: $vgs-max-width-content;
    margin: 0 auto;
    overflow-x: hidden;
  }

  .counter {
    margin-bottom: 4px;
  }

  .title {
    margin-bottom: 24px;
  }

  .description {
    display: flex;
    flex-direction: column;
    margin-bottom: 32px;

    .icon {
      margin-bottom: 8px;
    }

    :deep(.vgs-button) {
      margin-bottom: 24px;
      width: fit-content;
    }

    :deep(p:first-of-type) {
      margin-top: 0;
    }
  }

  .external-link-block {
    margin: 20px auto;
  }

  hr {
    margin: 24px 0;
  }

  .help {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    .ks-button {
      max-width: 100%;
    }
  }

  .help-title {
    width: 100%;
  }

  .expand-element {
    &:not(:last-of-type) {
      margin-bottom: 8px;
    }

    :deep(.cms-h3) {
      font-size: 1.125rem;
      font-weight: 700;
    }
  }
</style>
